// src/pages/Attendance/RegularizationCard.tsx
import React, { useState } from "react";
import { Calendar, Clock } from "lucide-react";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../store";
import { withdrawRegularization } from "../../store/slices/regularizeSlice";

interface RegularizationCardProps {
  application: any;
  type: "pending" | "history";
  fromHomeTab?: boolean;
}

const RegularizationCard: React.FC<RegularizationCardProps> = ({
  application,
  type,
  fromHomeTab,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [showConfirm, setShowConfirm] = useState(false);

  const isWithdrawVisible = type === "pending" && !fromHomeTab;

  const handleWithdrawClick = () => {
    setShowConfirm(true);
  };

const confirmWithdraw = async () => {
  try {
    await dispatch(withdrawRegularization(application._id)).unwrap();
  } catch (err: any) {
  } finally {
    setShowConfirm(false);
  }
};


  const cancelWithdraw = () => {
    setShowConfirm(false);
  };

  const { fromDate, startTime, endTime, reason, status, appliedOn } =
    application;

  return (
    <div className="bg-white border rounded-lg shadow-sm p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">
          Attendance Regularization
        </h3>
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            status === "approved"
              ? "bg-green-100 text-green-700"
              : status === "rejected"
              ? "bg-red-100 text-red-700"
              : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {status}
        </span>
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Calendar className="w-4 h-4" />
        <span>{fromDate}</span>
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>In: {startTime || "N/A"}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>Out: {endTime || "N/A"}</span>
        </div>
      </div>

      {reason && (
        <p className="text-sm text-gray-700">
          <span className="font-medium">Reason:</span> {reason}
        </p>
      )}

      {type === "history" && (
        <p className="text-xs text-gray-500">
          Submitted on {new Date(appliedOn).toLocaleDateString()}
        </p>
      )}

      {/* Withdraw button for pending applications */}
      {isWithdrawVisible && (
        <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={handleWithdrawClick}
            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition shadow-sm"
          >
            Withdraw
          </button>
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl w-[90%] max-w-md">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Confirm Withdrawal
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to withdraw this regularization application?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelWithdraw}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmWithdraw}
                className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600"
              >
                Yes, Withdraw
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegularizationCard;
